import React, { Component, createRef } from 'react';
import { Box, TextField } from '@mui/material';

class PillDateSelectComponent extends Component {
  constructor(props) {
    super(props);
    const { question } = props;
    this.state = {
      values: question.value || { year: '', month: '', day: '' },
      errors: { year: '', month: '', day: '' },
      showGroupError: false, // ← gate rendering of the single error line
    };
    this.refsMap = { year: createRef(), month: createRef(), day: createRef() };
  }

  componentDidMount() {
    const { name, registerValidation, values } = this.props;
    const externalValue = values?.[name];
    if (externalValue && typeof externalValue === 'object') {
      this.setState({
        values: { year: '', month: '', day: '', ...externalValue },
      });
    }
    registerValidation?.(name, this.triggerValidation);
    this.refsMap.year.current?.focus();
  }

  // Re-register on every update, the way the function-component fields do via
  // their effects. Registering only on mount was fragile: the engine clears its
  // validator map when the step changes, and if React reuses this instance
  // (same field key either side of the change) mount never runs again, so the
  // validator was gone and the Next button had nothing to check. Assigning the
  // same function again is idempotent.
  componentDidUpdate() {
    const { name, registerValidation } = this.props;
    registerValidation?.(name, this.triggerValidation);
  }

  validate = (value = this.state.values) => {
    const { question, dob, name } = this.props;
    const { year = '', month = '', day = '' } = value || {};
    const currentYear = new Date().getFullYear();
    const isDobField =
      name === 'birthday' || name === 'additionalDriverBirthday';
    // Policy holders must be at least 18 years old, and no older than 99.
    const minYear = isDobField ? currentYear - 99 : question.minYear || 1950;
    const maxYear = isDobField ? currentYear - 17 : currentYear;
    const options = question.options?.map((o) => o.value) || [
      'year',
      'month',
      'day',
    ];

    const errors = { year: '', month: '', day: '' };
    let isValid = true;

    if (options.includes('year')) {
      if (!/^\d{4}$/.test(year) || +year < minYear || +year > maxYear) {
        errors.year = `Year must be between ${minYear} and ${maxYear}`;
        isValid = false;
      }
    }

    if (options.includes('month')) {
      const m = parseInt(month, 10);
      if (!/^\d{2}$/.test(month) || m < 1 || m > 12) {
        errors.month = 'Enter a valid month (01–12)';
        isValid = false;
      }
    }

    if (options.includes('day')) {
      const date = new Date(`${year}-${month}-${day}`);
      const ok =
        /^\d{2}$/.test(day) &&
        (!options.includes('year') || date.getFullYear() === +year) &&
        (!options.includes('month') || date.getMonth() + 1 === +month) &&
        date.getDate() === +day;

      if (!ok) {
        errors.day = 'Invalid day for selected month/year';
        isValid = false;
      }
    }

    if (
      isValid &&
      dob &&
      this.props.minLicenseAge != null &&
      options.includes('year') &&
      options.includes('month')
    ) {
      const dobYear = parseInt(dob.year, 10);
      const dobMonth = parseInt(dob.month, 10);
      const licYear = parseInt(year, 10);
      const licMonth = parseInt(month, 10);

      if (!isNaN(dobYear) && !isNaN(dobMonth)) {
        const minAge = this.props.minLicenseAge;
        const minLicYear = dobYear + minAge;
        if (
          licYear < minLicYear ||
          (licYear === minLicYear && licMonth < dobMonth)
        ) {
          errors.year = `License must be obtained at least ${minAge} years after date of birth`;
          isValid = false;
        }
      }
    }

    this.setState({ errors });
    this.props.setValidationState?.(this.props.name, isValid);
    return isValid;
  };

  // Called by the engine when the primary Next button is clicked. Validates
  // first so the verdict can be returned synchronously — the engine blocks on
  // this return value, and deferring the check into the setState callback meant
  // an untouched, empty date could be read as valid on the first click.
  triggerValidation = () => {
    const isValid = this.validate();
    this.setState({ showGroupError: true });
    return isValid;
  };

  handleInputChange = (type) => (event) => {
    const { name, onChange } = this.props;
    const digitsOnly = event.target.value.replace(/\D/g, '');
    const input =
      type === 'year' ? digitsOnly.slice(0, 4) : digitsOnly.slice(0, 2);

    const updated = { ...this.state.values, [type]: input };
    this.setState({ values: updated }, () => {
      const ok = this.validate(updated);

      // If user starts typing and it’s invalid, reveal the single error line
      if (!ok && !this.state.showGroupError) {
        this.setState({ showGroupError: true });
      }

      onChange?.(name, updated);
      this.autoAdvance(type, input, updated);
    });
  };

  autoAdvance = (type, value, latestValues) => {
    const limits = { year: 4, month: 2, day: 2 };
    const nextField = { year: 'month', month: 'day' }[type];
    const options = this.props.question.options?.map((o) => o.value) || [
      'year',
      'month',
      'day',
    ];
    const {
      year = '',
      month = '',
      day = '',
    } = latestValues || this.state.values;

    if (value.length === limits[type] && nextField && this.refsMap[nextField]) {
      if (options.includes(nextField)) this.refsMap[nextField].current?.focus();
    }

    const allFilled =
      (!options.includes('year') || year.length === 4) &&
      (!options.includes('month') || month.length === 2) &&
      (!options.includes('day') || day.length === 2);

    if (allFilled) {
      const isValid = this.validate(latestValues || this.state.values);
      if (isValid && !this.props.question.disableAutoNext) {
        this.props.onNext?.();
      } else {
        this.setState({ showGroupError: true });
      }
    }
  };

  handleKeyDown = (type) => (e) => {
    if (e.key !== 'Enter') return;

    const limits = { year: 4, month: 2, day: 2 };
    const current = this.state.values[type] || '';
    const nextField = { year: 'month', month: 'day' }[type];
    const options = this.props.question.options?.map((o) => o.value) || [
      'year',
      'month',
      'day',
    ];
    const { year = '', month = '', day = '' } = this.state.values;

    if (current.length === limits[type]) {
      if (
        nextField &&
        this.refsMap[nextField]?.current &&
        options.includes(nextField)
      ) {
        this.refsMap[nextField].current.focus();
      } else {
        const allFilled =
          (!options.includes('year') || year.length === 4) &&
          (!options.includes('month') || month.length === 2) &&
          (!options.includes('day') || day.length === 2);

        const ok = this.validate(this.state.values);
        if (allFilled && ok && !this.props.question.disableAutoNext) {
          this.props.onNext?.();
        } else if (!ok) {
          this.setState({ showGroupError: true });
        }
      }
    } else {
      const ok = this.validate(this.state.values);
      if (!ok) this.setState({ showGroupError: true });
    }
  };

  renderField = (type, placeholder, width) => {
    const { values } = this.state;
    const { isMobile } = this.props;
    const label = type === 'year' ? 'YYYY' : type === 'month' ? 'MM' : 'DD';
    const hasValue = !!values?.[type]?.length;

    return (
      <Box
        key={type}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 'fit-content',
          minHeight: '80px',
        }}
      >
        <TextField
          variant="outlined"
          label={label}
          placeholder={placeholder}
          InputLabelProps={{
            shrink: true,
            'data-hasvalue': hasValue ? 'true' : 'false',
          }}
          InputProps={{ notched: false }}
          value={values[type]}
          onChange={this.handleInputChange(type)}
          inputRef={this.refsMap[type]}
          onKeyDown={this.handleKeyDown(type)}
          inputProps={{
            maxLength: type === 'year' ? 4 : 2,
            inputMode: 'numeric',
          }}
          error={false}
          helperText={'\u00A0'}
          sx={(theme) => {
            const PILL_HEIGHT = isMobile ? 53 : 56;
            return {
              width: width,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                height: PILL_HEIGHT,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #A2ACB1',
              },
              '& .MuiOutlinedInput-notchedOutline legend': {
                display: 'none',
                width: 0,
                padding: 0,
              },
              '& .MuiInputBase-input, & .MuiOutlinedInput-input': {
                height: PILL_HEIGHT,
                lineHeight: `${PILL_HEIGHT}px`,
                padding: 0,
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                fontSize: '1rem',
                fontWeight: 400,
                color: '#738289',
                boxSizing: 'border-box',
              },
              '& .MuiInputLabel-root': {
                position: 'absolute',
                left: 14,
                top: 20,
                fontSize: '0.75rem',
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                color: '#C7CA00',
                backgroundColor: '#fff',
                padding: '0 4px',
                transform: 'translate(0, 0) scale(1)',
                transformOrigin: 'top left',
                transition:
                  'transform 0.2s ease-in-out, opacity 0.2s ease-in-out',
                opacity: 0,
                pointerEvents: 'none',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                opacity: 1,
                transform: 'translate(0, -29px) scale(0.85)',
              },
              '& .MuiInputLabel-root[data-hasvalue="true"]': {
                opacity: 1,
                transform: 'translate(0, -29px) scale(0.85)',
              },
              '& .MuiInputLabel-root[data-hasvalue="true"]:not(.Mui-focused)': {
                opacity: 1,
                transform: 'translate(0, -29px) scale(0.85)',
                color: '#738289',
              },
              '& .MuiFormHelperText-root': {
                fontSize: '0.75rem',
                fontFamily: 'Inter, sans-serif',
                color: theme.palette?.component?.error || '#D32F2F',
                mt: '6px',
                textAlign: 'center',
                minHeight: '1em',
              },
              '& input::placeholder': { color: '#738289', opacity: 1 },
            };
          }}
        />
      </Box>
    );
  };

  render() {
    const { question, isMobile } = this.props;
    const { errors, showGroupError } = this.state;

    const options = question.options?.map((o) => o.value) || [
      'year',
      'month',
      'day',
    ];
    const firstError = Object.values(errors).find((e) => !!e);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          justifyContent: 'center',
          alignItems: 'center',
          mt: 3,
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: isMobile ? '100%' : 'auto',
          px: isMobile ? '50px' : 0,
          boxSizing: 'border-box',
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {options.includes('year') &&
            this.renderField('year', 'YYYY', isMobile ? '83px' : '110px')}
          {options.includes('month') &&
            this.renderField('month', 'MM', isMobile ? '66px' : '74px')}
          {options.includes('day') &&
            this.renderField('day', 'DD', isMobile ? '66px' : '74px')}
        </Box>

        {showGroupError && firstError && (
          <Box sx={{ mt: 1 }}>
            <span
              style={{
                fontSize: '0.75rem',
                fontFamily: 'Inter, sans-serif',
                color: '#D32F2F',
                textAlign: 'center',
                display: 'block',
              }}
            >
              {firstError}
            </span>
          </Box>
        )}
      </Box>
    );
  }
}

export default PillDateSelectComponent;
